import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs';
import { FileService } from 'src/app/services/api/file.service';
import {
  DialogSyncPaymentDateResult,
  MASS_OS_B2C_SOURCE_OPTIONS,
  MASS_OS_MODE_STEPS,
  MASS_OS_MONTH_OPTIONS,
  MASS_OS_SERVICE_TYPE_OPTIONS,
  MASS_OS_SERVICE_TYPE_TO_QUERY,
  MassOsB2bClient,
  MassOsCurrency,
  MassOsGenerationForm,
  MassOsGenerationMode,
  MassOsItinerary,
  MassOsItineraryPaymentState,
  MassOsItineraryService,
  MassOsPaymentCondition,
  MassOsPeriodMode,
  MassOsProviderOption,
  MassOsServicePaymentState,
  MassOsTicket,
  buildInitialMassOsGenerationForm,
  massOsServiceTypeLabel,
  toMassOsServiceOrigin
} from 'src/entities/file';
import { FileProgrammingGraphqlApi } from 'src/entities/file/api/file-programming.graphql-api';
import {
  GetAllProviderResponse,
  MassOsGenerationGraphqlApi
} from 'src/entities/file/api/mass-os-generation.graphql-api';
import { applyCreatedPurchaseNotesToServices } from 'src/entities/purchase-note';
import { PurchaseNotePdfDialogComponent } from 'src/features/purchase-note/purchase-note-pdf/ui/purchase-note-pdf-dialog/purchase-note-pdf-dialog.component';
import { buildPurchaseNotePdfDialogConfig } from 'src/features/purchase-note/purchase-note-pdf/ui/purchase-note-pdf-dialog/purchase-note-pdf-dialog.config';
import { OrderPdfDialogComponent } from 'src/features/service-order/order-pdf/ui/order-pdf-dialog/order-pdf-dialog.component';
import { buildOrderPdfDialogConfig } from 'src/features/service-order/order-pdf/ui/order-pdf-dialog/order-pdf-dialog.config';
import { MassOsItinerariesTableComponent } from 'src/widgets/mass-os-itineraries-table/mass-os-itineraries-table.component';
import { DialogSyncPaymentDateComponent } from './dialogs/dialog-sync-payment-date/dialog-sync-payment-date.component';

/** Unidad de pago dentro de una OS: 1 por servicio o 1 por ticket (TICKET). */
interface OsUnitState {
  kind: 'service' | 'ticket';
  serviceId: string;
  ticketId?: string | null;
  moneda: MassOsCurrency;
  total: number;
  nombreServicio: string;
  nombre: string;
  serviceDay: number;
  fecha: Date;
  paymentDate: Date;
  codigoReserva: string | null;
  observaciones?: string | null;
}

/** Orden de Servicio (agrupada por File → Fecha → Moneda → Código de Reserva). */
interface OsGroup {
  fileNumber: string;
  moneda: MassOsCurrency;
  fechaPago: Date;
  numOS: string | null;
  orderId: string | null;
  purchaseNoteId: string | null;
  purchaseNoteNumber: string | null;
  codigoReserva: string | null;
  observaciones: string;
  services: MassOsItineraryService[];
  units: OsUnitState[];
  total: number;
  commissionPercent?: number | null;
  commissionAmount?: number | null;
  payableTotal?: number | null;
  commissionLabel?: string | null;
}

interface OsMonedaGroup {
  moneda: MassOsCurrency;
  codigoGrupos: OsGroup[];
}

interface OsFechaPagoGroup {
  fechaPago: Date;
  monedaGrupos: OsMonedaGroup[];
}

interface OsFileGroup {
  fileNumber: string;
  fechaGrupos: OsFechaPagoGroup[];
}

@Component({
  selector: 'app-mass-os-generation',
  templateUrl: './mass-os-generation.component.html',
  styleUrls: ['./styles/mass-os-generation.layout.css']
})
export class MassOsGenerationComponent implements OnInit {
  mode: MassOsGenerationMode = 'os';
  get steps(): string[] {
    return MASS_OS_MODE_STEPS[this.mode];
  }
  itineraries: MassOsItinerary[] = [];

  /** Estado de paginación (patrón idéntico a file-programming.component.ts). */
  pageIndex = 0;
  pageSize = 10;
  /** Cursors por página: pageCursors[0] = null, pageCursors[i] = nextCursor de la página i-1. */
  private pageCursors: Array<string | null> = [null];
  private nextCursor: string | null = null;
  /** Cache de itinerarios ya cargados (todas las páginas) para la selección cruzada de páginas. */
  private itineraryCache = new Map<string, MassOsItinerary>();
  /** Comisión aplicada al crear, por serviceId. El monto base del grupo sigue en `total`. */
  private commissionByServiceId = new Map<
    string,
    { percent: number | null; amount: number }
  >();

  @ViewChild('itinerariesTable')
  itinerariesTable!: MassOsItinerariesTableComponent;

  providers: MassOsProviderOption[] = [];
  b2bClients: MassOsB2bClient[] = [];

  activeStep = 0;
  stepOneSubmitted = false;
  /** Mensaje global de validación del paso 1 (se muestra al intentar continuar). */
  stepOneError: string | null = null;
  selectedServiceCount = 0;
  /** Tickets seleccionados (crear OS por ticket) alimentando el resumen. */
  selectedTicketCount = 0;
  /** Ids de servicios seleccionados para OS (capturados al pasar al paso 3). */
  selectedServiceIdsForOs: string[] = [];
  form: MassOsGenerationForm = buildInitialMassOsGenerationForm();
  isLoadingProviders = false;
  isLoadingClients = false;
  isLoadingItineraries = false;
  readonly activeFilterCount = 0;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly api: MassOsGenerationGraphqlApi,
    private readonly fileApi: FileProgrammingGraphqlApi,
    private readonly fileService: FileService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.mode =
      (this.route.snapshot.data['mode'] as MassOsGenerationMode) ?? 'os';
    this.loadProviders();
    this.loadB2bClients();
  }

  get isLastStep(): boolean {
    return this.activeStep === this.steps.length - 1;
  }

  /** Longitud del paginador (misma estimación que file-programming: página
   *  actual + una página extra mientras haya nextCursor). No revela el total
   *  real; el paginador solo sugiere la página siguiente. */
  get paginatorLength(): number {
    return (
      this.pageIndex * this.pageSize +
      this.itineraries.length +
      (this.nextCursor ? this.pageSize : 0)
    );
  }

  get isStepOneValid(): boolean {
    if (!this.form.providerId) {
      return false;
    }

    if (this.form.periodMode === 'range') {
      if (!this.form.rangeStart || !this.form.rangeEnd) {
        return false;
      }
    } else if (this.form.year === null || !this.form.months.length) {
      return false;
    }

    if (!this.form.b2bClientIds.length) {
      return false;
    }

    if (!this.form.serviceTypeIds.length) {
      return false;
    }

    return true;
  }

  /** Chips de filtros aplicados en el paso 1 (listos para el resumen del paso 2). */
  get appliedFilterChips(): Array<{ id: string; label: string }> {
    const chips: Array<{ id: string; label: string }> = [];

    const provider = this.providers.find((p) => p.id === this.form.providerId);
    if (provider) {
      chips.push({ id: 'provider', label: `Proveedor: ${provider.name}` });
    }

    if (this.form.periodMode === 'range') {
      const fmt = (d: Date | null): string =>
        d
          ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
          : '';
      if (this.form.rangeStart || this.form.rangeEnd) {
        chips.push({
          id: 'periodo',
          label: `Periodo: ${fmt(this.form.rangeStart)} - ${fmt(this.form.rangeEnd)}`
        });
      }
    } else if (this.form.year !== null && this.form.months.length) {
      const monthLabels = MASS_OS_MONTH_OPTIONS.filter((m) =>
        this.form.months.includes(m.value)
      )
        .map((m) => m.label)
        .join(', ');
      chips.push({
        id: 'periodo',
        label: `Periodo: ${monthLabels} ${this.form.year}`
      });
    }

    if (this.form.clientMode === 'b2b') {
      const clients = this.b2bClients
        .filter((c) => this.form.b2bClientIds.includes(c.id))
        .map((c) => c.name);
      if (clients.length) {
        chips.push({ id: 'cliente', label: `Cliente: ${clients.join(', ')}` });
      }
    } else if (this.form.b2cSourceIds.length) {
      const sources = MASS_OS_B2C_SOURCE_OPTIONS.filter((s) =>
        this.form.b2cSourceIds.includes(s.id)
      ).map((s) => s.label);
      if (sources.length) {
        chips.push({
          id: 'cliente',
          label: `Origen: ${sources.join(', ')}`
        });
      }
    }

    if (this.form.serviceTypeIds.length) {
      const types = MASS_OS_SERVICE_TYPE_OPTIONS.filter((t) =>
        this.form.serviceTypeIds.includes(t.id)
      ).map((t) => t.label);
      if (types.length) {
        chips.push({
          id: 'servicios',
          label: `Servicios: ${types.join(', ')}`
        });
      }
    }

    return chips;
  }

  onStepSelected(index: number): void {
    // "1 -> 2 -> 3 -> 1": desde el paso de emisión (3) no se puede volver al
    // paso de revisión (2); solo reiniciar en el 1.
    if (this.activeStep === 2 && index === 1) {
      return;
    }
    this.activeStep = index;
    if (index === 0) {
      this.stepOneSubmitted = false;
      this.stepOneError = null;
    }
  }

  trackFilterChip = (_index: number, chip: { id: string }): string => chip.id;

  onStepOneChange(patch: Partial<MassOsGenerationForm>): void {
    this.stepOneError = null;
    const next: MassOsGenerationForm = { ...this.form, ...patch };

    if (patch.periodMode && patch.periodMode !== this.form.periodMode) {
      this.stepOneSubmitted = false;
      if (patch.periodMode === 'range') {
        next.year = null;
        next.months = [];
      } else {
        next.rangeStart = null;
        next.rangeEnd = null;
      }
    }

    this.form = next;
  }

  onPeriodModeChange(periodMode: MassOsPeriodMode): void {
    this.onStepOneChange({ periodMode });
  }

  continueToNextStep(): void {
    if (this.isLastStep) {
      return;
    }

    if (this.activeStep === 0) {
      this.stepOneSubmitted = true;
      const missing = this.firstInvalidField();
      this.stepOneError = missing ? this.describeMissingField(missing) : null;
      if (missing) {
        this.focusFirstInvalidField(missing);
        return;
      }
      this.loadProgrammingItineraries();
      return;
    }

    this.activeStep += 1;
  }

  /** Mensaje global que describe el primer campo faltante del paso 1. */
  private describeMissingField(
    field: 'provider' | 'period' | 'client' | 'services'
  ): string {
    switch (field) {
      case 'provider':
        return 'Selecciona un proveedor para continuar.';
      case 'period':
        return 'Completa el periodo (año y meses, o rango de fechas) para continuar.';
      case 'client':
        return 'Selecciona al menos un cliente para continuar.';
      case 'services':
        return 'Selecciona al menos un tipo de servicio para continuar.';
      default:
        return 'Completa los campos obligatorios para continuar.';
    }
  }

  /**
   * Jerarquía de campos del paso 1: proveedor → periodo → cliente → servicios.
   * Devuelve el primer campo incompleto (null si el formulario está completo).
   */
  private firstInvalidField():
    | 'provider'
    | 'period'
    | 'client'
    | 'services'
    | null {
    if (!this.form.providerId) {
      return 'provider';
    }

    if (this.form.periodMode === 'range') {
      if (!this.form.rangeStart || !this.form.rangeEnd) {
        return 'period';
      }
    } else if (this.form.year === null || !this.form.months.length) {
      return 'period';
    }

    if (!this.form.b2bClientIds.length) {
      return 'client';
    }

    if (!this.form.serviceTypeIds.length) {
      return 'services';
    }

    return null;
  }

  /** Lleva al usuario al primer campo incompleto (scroll suave + resaltado breve). */
  private focusFirstInvalidField(
    field: 'provider' | 'period' | 'client' | 'services'
  ): void {
    const idMap: Record<string, string> = {
      provider: 'mass-os-provider',
      period: 'mass-os-periodo',
      client: 'mass-os-client',
      services: 'mass-os-service-types'
    };
    const el = document.getElementById(idMap[field]);
    if (!el) {
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('mass-os-card--flash');
    window.setTimeout(() => el.classList.remove('mass-os-card--flash'), 1600);
  }

  onRefresh(): void {
    this.loadProviders();
    this.loadB2bClients();
  }

  onSyncPaymentDate(): void {
    if (this.selectedServiceCount === 0) {
      return;
    }

    const selectedServiceIds = this.itinerariesTable?.selectedIdList ?? [];
    this.selectedServiceIdsForOs = selectedServiceIds;
    const selectedTickets = this.itinerariesTable?.selectedTickets ?? {};
    const selectedProvider = this.providers.find(
      (p) => p.id === this.form.providerId
    );
    // Con paginación el array visible es una página; el dialog recibe la unión
    // de itinerarios seleccionados de todas las páginas ya cargadas (cache).
    const selectedItineraries =
      this.selectedItinerariesFromCache(selectedServiceIds);

    this.dialog
      .open(DialogSyncPaymentDateComponent, {
        width: '90vw',
        height: '90vh',
        maxWidth: '90vw',
        maxHeight: '90vh',
        panelClass: 'sync-payment-date-panel',
        autoFocus: false,
        data: {
          itineraries: selectedItineraries,
          selectedServiceIds,
          selectedTickets,
          provider: selectedProvider ?? null,
          mode: this.mode
        }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.confirmed) {
          for (const itState of result.itineraryStates) {
            const itinerary = this.itineraryCache.get(itState.itineraryId);
            if (!itinerary) continue;

            for (const svcState of itState.serviceStates) {
              const svc = itinerary.servicios.find(
                (s) => s.id === svcState.serviceId
              );
              if (!svc) continue;

              svc.fechaPagoProgramada = this.getFinalDate(svcState, itState);
              svc.codigoReserva = svcState.codigoReserva;
              svc.observaciones = svcState.observaciones;

              if (svcState.isTicket && svcState.tickets?.length) {
                svc.tickets = this.mapPaymentTicketsToService(
                  svcState.tickets,
                  itState
                );
              }
            }
          }
          // Reconstruye el array con los itinerarios confirmados (pueden venir
          // de varias páginas) para que el resumen del paso 3 los contemple.
          if (result.itineraryStates.length) {
            this.itineraries = this.confirmedItineraries(
              result.itineraryStates
            );
          }
          this.trimToSelectedServices(result.itineraryStates);
          if (this.mode === 'os') {
            this.applyCreatedOrdersToServices(result.createdOrders);
          } else {
            this.applyCreatedPurchaseNotesToServices(
              result.createdPurchaseNotes
            );
          }
          this.resetTablePagination();
          this.activeStep = 2;
        }
      });
  }

  /** Itinerarios de la cache que tienen al menos un servicio seleccionado. */
  private selectedItinerariesFromCache(
    selectedServiceIds: string[]
  ): MassOsItinerary[] {
    if (!selectedServiceIds.length) {
      return [];
    }
    const set = new Set(selectedServiceIds);
    return Array.from(this.itineraryCache.values()).filter((it) =>
      it.servicios.some((s) => set.has(String(s.id)))
    );
  }

  /** Itinerarios confirmados en el dialog (presentes en itineraryStates). */
  private confirmedItineraries(
    itineraryStates: MassOsItineraryPaymentState[]
  ): MassOsItinerary[] {
    const ids = new Set(itineraryStates.map((s) => s.itineraryId));
    return Array.from(this.itineraryCache.values()).filter((it) =>
      ids.has(it.id)
    );
  }

  /** Convierte el estado de pago por ticket del dialog a la forma de la grilla (para el resumen). */
  private mapPaymentTicketsToService(
    tickets: Array<{
      ticketId: string;
      total: number;
      moneda: MassOsCurrency;
      fechaAutomatica: Date;
      fechaManual: Date | null;
      codigoReserva?: string | null;
      observaciones?: string | null;
    }>,
    itState: MassOsItineraryPaymentState
  ): MassOsTicket[] {
    return tickets.map((t) => ({
      id: t.ticketId,
      total: t.total,
      moneda: t.moneda,
      codigoReserva: t.codigoReserva ?? null,
      observaciones: t.observaciones ?? null,
      fechaPagoProgramada:
        t.fechaManual ?? itState.fechaMasiva ?? t.fechaAutomatica,
      order: null
    }));
  }

  private rememberCommission(
    serviceIds: string[] | null | undefined,
    source: {
      commissionPercent?: number | null;
      commissionAmount?: number | null;
    }
  ): void {
    if (source.commissionAmount == null) {
      return;
    }
    const snapshot = {
      percent:
        source.commissionPercent == null
          ? null
          : Number(source.commissionPercent),
      amount: Number(source.commissionAmount)
    };
    for (const serviceId of serviceIds ?? []) {
      this.commissionByServiceId.set(String(serviceId), snapshot);
    }
  }

  /** Comisión del grupo a partir de la OS/NP ya creada. El monto base es `group.total`. */
  private payForGroup(group: OsGroup): {
    percent: number | null;
    amount: number;
    total: number;
    label: string;
  } | null {
    let stored: { percent: number | null; amount: number } | undefined;
    for (const svc of group.services) {
      stored = this.commissionByServiceId.get(String(svc.id));
      if (stored) {
        break;
      }
    }
    if (!stored || !Number.isFinite(stored.amount)) {
      return null;
    }
    const base = group.total;
    const percent = stored.percent;
    const amount =
      this.mode === 'os' && percent != null && Number.isFinite(percent)
        ? this.roundMoney(
            group.units.reduce(
              (sum, unit) =>
                sum + this.roundMoney((unit.total * percent) / 100),
              0
            )
          )
        : percent != null && Number.isFinite(percent)
          ? this.roundMoney((base * percent) / 100)
          : stored.amount;
    const percentText =
      percent == null || !Number.isFinite(percent)
        ? null
        : Number.isInteger(percent)
          ? String(percent)
          : String(percent);
    return {
      percent,
      amount,
      total: this.roundMoney(base + amount),
      label: percentText == null ? 'Comisión' : `Comisión (${percentText}%)`
    };
  }

  /** Comisión de una fila del resumen OS. El monto fijo se reparte en partes iguales. */
  unitPay(
    unit: OsUnitState,
    group: OsGroup,
    index: number
  ): { commission: number; total: number } | null {
    if (this.mode !== 'os' || group.commissionAmount == null) {
      return null;
    }
    const percent = group.commissionPercent;
    if (percent != null && Number.isFinite(Number(percent))) {
      const commission = this.roundMoney(
        (unit.total * Number(percent)) / 100
      );
      return {
        commission,
        total: this.roundMoney(unit.total + commission)
      };
    }
    const count = group.units.length;
    if (count <= 0) {
      return null;
    }
    const totalCommission = this.roundMoney(Number(group.commissionAmount));
    const share = this.roundMoney(totalCommission / count);
    const commission =
      index === count - 1
        ? this.roundMoney(totalCommission - share * (count - 1))
        : share;
    return {
      commission,
      total: this.roundMoney(unit.total + commission)
    };
  }

  private roundMoney(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  /** Estampa el número de OS devuelto por el backend en los servicios locales. */
  private applyCreatedOrdersToServices(
    createdOrders?: Array<{
      orderId: string;
      orderNumber: string;
      serviceIds: string[];
      ticketIds?: string[] | null;
      commissionPercent?: number | null;
      commissionAmount?: number | null;
    }> | null
  ): void {
    if (!createdOrders?.length) {
      return;
    }

    const byServiceId = new Map<
      string,
      {
        orderId: string;
        orderNumber: string;
        displayCode?: string | null;
        orderStatus?: string | null;
      }
    >();
    for (const order of createdOrders) {
      this.rememberCommission(order.serviceIds, order);
      for (const serviceId of order.serviceIds ?? []) {
        byServiceId.set(String(serviceId), order);
      }
    }
    if (!byServiceId.size) {
      return;
    }

    for (const itinerary of this.itineraries) {
      for (const svc of itinerary.servicios) {
        const order = byServiceId.get(String(svc.id));
        if (order) {
          svc.numOS =
            order.displayCode ||
            (order.orderStatus === 'PRELIMINAR'
              ? `P-${order.orderNumber}`
              : order.orderNumber);
          svc.orderId = order.orderId;
        }
      }
    }

    this.applyCommissionToLocalCosts(createdOrders);
    this.commissionByServiceId.clear();
  }

  /**
   * El monto que se muestra pasa a ser el monto a pagar.
   * Si no, la grilla sigue en el costo anterior hasta recargar.
   */
  private applyCommissionToLocalCosts(
    createdOrders: Array<{
      serviceIds: string[];
      ticketIds?: string[] | null;
      commissionPercent?: number | null;
      commissionAmount?: number | null;
    }>
  ): void {
    for (const order of createdOrders) {
      const commissionAmount = Number(order.commissionAmount);
      if (!Number.isFinite(commissionAmount) || commissionAmount <= 0) {
        continue;
      }
      const ticketIds = new Set(
        (order.ticketIds ?? []).map((id) => String(id))
      );
      const serviceIds = new Set(
        (order.serviceIds ?? []).map((id) => String(id))
      );
      const percent =
        order.commissionPercent != null &&
        Number.isFinite(Number(order.commissionPercent))
          ? Number(order.commissionPercent)
          : null;
      const unitCount = ticketIds.size > 0 ? ticketIds.size : serviceIds.size;
      const fixedEach =
        percent == null && unitCount > 0
          ? this.roundMoney(commissionAmount / unitCount)
          : null;

      for (const itinerary of this.itineraries) {
        for (const svc of itinerary.servicios) {
          if (svc.tickets?.length && ticketIds.size > 0) {
            let touched = false;
            for (const ticket of svc.tickets) {
              if (!ticketIds.has(String(ticket.id))) {
                continue;
              }
              const next = this.payableAmount(
                Number(ticket.total) || 0,
                percent,
                fixedEach
              );
              if (next == null) {
                continue;
              }
              ticket.total = next;
              touched = true;
            }
            if (touched) {
              const sum = this.roundMoney(
                svc.tickets.reduce(
                  (acc, ticket) => acc + (Number(ticket.total) || 0),
                  0
                )
              );
              svc.costo = sum;
              svc.total = sum;
            }
            continue;
          }
          if (!serviceIds.has(String(svc.id)) || ticketIds.size > 0) {
            continue;
          }
          const next = this.payableAmount(
            Number(svc.costo) || 0,
            percent,
            fixedEach
          );
          if (next == null) {
            continue;
          }
          svc.costo = next;
          svc.total = next;
        }
      }
    }
  }

  private payableAmount(
    base: number,
    percent: number | null,
    fixedEach: number | null
  ): number | null {
    if (!Number.isFinite(base)) {
      return null;
    }
    const extra =
      percent != null
        ? this.roundMoney((base * percent) / 100)
        : fixedEach != null
          ? fixedEach
          : null;
    if (extra == null) {
      return null;
    }
    const payable = this.roundMoney(base + extra);
    return payable === this.roundMoney(base) ? null : payable;
  }

  /** Aplica las NP creadas a los servicios locales (mismo patrón que OS). */
  private applyCreatedPurchaseNotesToServices(
    createdPurchaseNotes?: DialogSyncPaymentDateResult['createdPurchaseNotes']
  ): void {
    if (!createdPurchaseNotes?.length) {
      return;
    }

    for (const note of createdPurchaseNotes) {
      this.rememberCommission(note.serviceIds, note);
    }

    const services = this.itineraries.flatMap((it) => it.servicios);
    applyCreatedPurchaseNotesToServices(services, createdPurchaseNotes);
  }

  /** Deja en cada itinerario únicamente los servicios confirmados para OS. */
  private trimToSelectedServices(
    itineraryStates: MassOsItineraryPaymentState[]
  ): void {
    const selectedIds = new Set<string>();
    for (const it of itineraryStates) {
      for (const svc of it.serviceStates) {
        selectedIds.add(svc.serviceId);
      }
    }
    if (selectedIds.size === 0) {
      return;
    }
    for (const itinerary of this.itineraries) {
      itinerary.servicios = itinerary.servicios.filter((s) =>
        selectedIds.has(s.id)
      );
    }
  }

  private getFinalDate(
    svcState: MassOsServicePaymentState,
    itState?: MassOsItineraryPaymentState
  ): Date {
    if (svcState.fechaManual) return svcState.fechaManual;
    if (itState?.fechaMasiva) return itState.fechaMasiva;
    return svcState.fechaAutomatica;
  }

  /* ─── Paso 3: Resumen de Órdenes de Servicio ─── */

  /** OS agrupada por File → Fecha de Pago → Moneda → Código de Reserva (misma distribución que el dialog). */
  get fileOsGroups(): OsFileGroup[] {
    const fileMap = new Map<
      string,
      Map<string, Map<string, Map<string, OsGroup>>>
    >();

    for (const it of this.itineraries) {
      for (const svc of it.servicios) {
        if (!this.selectedServiceIds.includes(svc.id)) {
          continue;
        }
        const units = this.paymentUnits(svc);
        for (const unit of units) {
          const fecha = unit.paymentDate;
          const fechaKey = `${fecha.getTime()}`;
          const codigo = unit.codigoReserva?.trim() || '';

          if (!fileMap.has(it.fileNumber)) {
            fileMap.set(it.fileNumber, new Map());
          }
          const fechaMap = fileMap.get(it.fileNumber)!;
          if (!fechaMap.has(fechaKey)) {
            fechaMap.set(fechaKey, new Map());
          }
          const monedaMap = fechaMap.get(fechaKey)!;
          if (!monedaMap.has(unit.moneda)) {
            monedaMap.set(unit.moneda, new Map());
          }
          const codigoMap = monedaMap.get(unit.moneda)!;
          if (!codigoMap.has(codigo)) {
            codigoMap.set(codigo, {
              fileNumber: it.fileNumber,
              moneda: unit.moneda,
              codigoReserva: codigo,
              fechaPago: fecha,
              numOS: null,
              orderId: null,
              purchaseNoteId: null,
              purchaseNoteNumber: null,
              observaciones: '',
              services: [],
              units: [],
              total: 0
            });
          }
          const group = codigoMap.get(codigo)!;
          if (!group.services.some((s) => s.id === svc.id)) {
            group.services.push(svc);
            group.numOS = group.numOS ?? svc.numOS ?? null;
            group.orderId = group.orderId ?? svc.orderId ?? null;
            group.purchaseNoteId =
              group.purchaseNoteId ??
              svc.purchaseNote?.purchaseNoteId ??
              svc.purchaseNoteId ??
              null;
            group.purchaseNoteNumber =
              group.purchaseNoteNumber ??
              svc.purchaseNote?.purchaseNoteNumber ??
              null;
            if (!group.observaciones && svc.observaciones) {
              group.observaciones = svc.observaciones;
            }
          }
          group.units.push(unit);
          group.total += unit.total;
          if (!group.observaciones && unit.observaciones) {
            group.observaciones = unit.observaciones;
          }
        }
      }
    }

    const result: OsFileGroup[] = [];
    const files = Array.from(fileMap.keys()).sort();
    for (const fileNumber of files) {
      const fechaMap = fileMap.get(fileNumber)!;
      const fechaKeys = Array.from(fechaMap.keys()).sort(
        (a, b) => Number(a) - Number(b)
      );
      const fechaGrupos: OsFechaPagoGroup[] = fechaKeys.map((fechaKey) => {
        const monedaMap = fechaMap.get(fechaKey)!;
        const monedaKeys = Array.from(monedaMap.keys()).sort();
        const monedaGrupos: OsMonedaGroup[] = monedaKeys.map((moneda) => {
          const codigoMap = monedaMap.get(moneda)!;
          const codigoKeys = Array.from(codigoMap.keys()).sort();
          const codigoGrupos = codigoKeys.map((codigo) => {
            const group = codigoMap.get(codigo)!;
            const pay = this.payForGroup(group);
            group.commissionPercent = pay?.percent ?? null;
            group.commissionAmount = pay?.amount ?? null;
            group.payableTotal = pay?.total ?? null;
            group.commissionLabel = pay?.label ?? null;
            return group;
          });
          return { moneda: moneda as MassOsCurrency, codigoGrupos };
        });
        return {
          fechaPago: monedaGrupos[0]?.codigoGrupos[0]?.fechaPago ?? new Date(),
          monedaGrupos
        };
      });
      result.push({ fileNumber, fechaGrupos });
    }
    return result;
  }

  /** Unidades de pago del servicio: 1 por ticket (TICKET) o 1 por servicio. */
  private paymentUnits(svc: MassOsItineraryService): OsUnitState[] {
    if (svc.tickets?.length) {
      return svc.tickets.map((t) => ({
        kind: 'ticket' as const,
        serviceId: svc.id,
        ticketId: t.id ?? null,
        moneda: (t.moneda as MassOsCurrency | null | undefined) ?? svc.moneda,
        total: Number(t.total) || 0,
        nombreServicio: svc.nombreServicio,
        nombre: svc.nombre,
        serviceDay: svc.serviceDay,
        fecha: svc.fecha,
        paymentDate: t.fechaPagoProgramada ?? this.servicePaymentDate(svc),
        codigoReserva: t.codigoReserva ?? svc.codigoReserva ?? null,
        observaciones: t.observaciones ?? null
      }));
    }
    return [
      {
        kind: 'service' as const,
        serviceId: svc.id,
        moneda: svc.moneda,
        total: svc.costo,
        nombreServicio: svc.nombreServicio,
        nombre: svc.nombre,
        serviceDay: svc.serviceDay,
        fecha: svc.fecha,
        paymentDate: this.servicePaymentDate(svc),
        codigoReserva: svc.codigoReserva ?? null,
        observaciones: svc.observaciones ?? null
      }
    ];
  }

  private servicePaymentDate(svc: MassOsItineraryService): Date {
    return svc.fechaPagoProgramada ?? new Date();
  }

  /** Total de OS dentro de una fecha de pago (equivale a los grupos de código). */
  fechaOsCount(fechaGroup: OsFechaPagoGroup): number {
    return fechaGroup.monedaGrupos.reduce(
      (sum, moneda) => sum + moneda.codigoGrupos.length,
      0
    );
  }

  get totalOs(): number {
    return this.fileOsGroups.reduce(
      (sum, file) =>
        sum +
        file.fechaGrupos.reduce(
          (s, f) =>
            s + f.monedaGrupos.reduce((ss, m) => ss + m.codigoGrupos.length, 0),
          0
        ),
      0
    );
  }

  get totalServices(): number {
    const ids = new Set<string>();
    for (const file of this.fileOsGroups) {
      for (const f of file.fechaGrupos) {
        for (const m of f.monedaGrupos) {
          for (const g of m.codigoGrupos) {
            for (const svc of g.services) {
              ids.add(svc.id);
            }
          }
        }
      }
    }
    return ids.size;
  }

  get currencyTotals(): Array<{ moneda: MassOsCurrency; total: number }> {
    const totals = new Map<MassOsCurrency, number>();
    for (const file of this.fileOsGroups) {
      for (const f of file.fechaGrupos) {
        for (const monedaGroup of f.monedaGrupos) {
          for (const g of monedaGroup.codigoGrupos) {
            totals.set(
              g.moneda,
              (totals.get(g.moneda) ?? 0) + (g.payableTotal ?? g.total)
            );
          }
        }
      }
    }
    return Array.from(totals.entries()).map(([moneda, total]) => ({
      moneda,
      total
    }));
  }

  private get selectedServiceIds(): string[] {
    return this.selectedServiceIdsForOs;
  }

  formatShortDate(date: Date): string {
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  }

  /** Abre el PDF de la OS o NP según el modo actual. */
  verPdf(group: OsGroup): void {
    if (this.mode === 'os') {
      const orderId = group?.orderId?.trim();
      if (!orderId) {
        return;
      }

      this.dialog
        .open(
          OrderPdfDialogComponent,
          buildOrderPdfDialogConfig({
            orderId,
            orderNumber: group.numOS,
            pdfStatus: null,
            lastPdfError: null
          })
        )
        .afterClosed()
        .subscribe();
      return;
    }

    const purchaseNoteId = group?.purchaseNoteId?.trim();
    if (!purchaseNoteId) {
      return;
    }

    this.dialog
      .open(
        PurchaseNotePdfDialogComponent,
        buildPurchaseNotePdfDialogConfig({
          purchaseNoteId,
          purchaseNoteNumber: group.purchaseNoteNumber,
          pdfStatus: null,
          lastPdfError: null
        })
      )
      .afterClosed()
      .subscribe();
  }

  private loadProgrammingItineraries(): void {
    this.resetTablePagination();
    this.itineraryCache.clear();
    this.loadItinerariesPage(true, true);
  }

  /** Paginador de la tabla de itinerarios (mismo contrato que programación). */
  onTablePageChange(event: PageEvent): void {
    if (event.pageSize !== this.pageSize) {
      this.pageSize = event.pageSize;
      this.loadItinerariesPage(true, false);
      return;
    }

    this.pageIndex = event.pageIndex;
    this.loadItinerariesPage(false, false);
  }

  /**
   * Carga la página actual (página 0 al resetear) encadenando nextCursor.
   * `advanceStep` solo es true en la carga inicial (desde el paso 1); el
   * paginador nunca debe avanzar al step 3.
   */
  private loadItinerariesPage(resetPage: boolean, advanceStep: boolean): void {
    if (resetPage) {
      this.pageIndex = 0;
      this.pageCursors = [null];
      this.nextCursor = null;
    }
    const cursor = this.pageCursors[this.pageIndex] ?? null;
    const input = this.buildProgrammingItinerariesInput(cursor, this.pageSize);
    this.isLoadingItineraries = true;
    this.api
      .getProgrammingServicesPage(input)
      .pipe(
        finalize(() => {
          this.isLoadingItineraries = false;
        })
      )
      .subscribe({
        next: (data: any) => {
          const page = data?.getProgrammingServicesPage;
          const items = page?.items ?? [];
          this.nextCursor = page?.nextCursor ?? null;
          if (this.nextCursor) {
            this.pageCursors[this.pageIndex + 1] = this.nextCursor;
          } else {
            this.pageCursors = this.pageCursors.slice(0, this.pageIndex + 1);
          }
          this.itineraries = items.map((item: any) => this.mapItinerary(item));
          for (const itinerary of this.itineraries) {
            this.itineraryCache.set(itinerary.id, itinerary);
          }
          this.preloadServices(this.itineraries, advanceStep);
        },
        error: (err) =>
          console.error('Error cargando itinerarios programados', err)
      });
  }

  private mapItinerary(item: any): MassOsItinerary {
    return {
      id: item.id,
      fileNumber: item.fileNumber,
      agencia: item.nameClient,
      usuario: item.displayState,
      clientType: 'b2b' as const,
      serviceIds: Array.isArray(item.serviceIds) ? item.serviceIds : [],
      servicios: []
    };
  }

  private resetTablePagination(): void {
    this.pageIndex = 0;
    this.pageSize = 10;
    this.pageCursors = [null];
    this.nextCursor = null;
  }

  /**
   * Precarga los detalles de los servicios (sin depender del expand) usando
   * getProgrammingServicesByIds, y avanza al paso de revisión cuando termina
   * (solo en la carga inicial/reset; al cambiar de página no se avanza de paso).
   */
  private preloadServices(
    itineraries: MassOsItinerary[],
    advanceStep: boolean
  ): void {
    const allServiceIds = Array.from(
      new Set(itineraries.flatMap((it) => it.serviceIds ?? []))
    );
    if (!allServiceIds.length) {
      if (advanceStep) {
        this.activeStep += 1;
      }
      return;
    }

    this.isLoadingItineraries = true;
    this.api
      .getProgrammingServicesByIds(allServiceIds)
      .pipe(
        finalize(() => {
          this.isLoadingItineraries = false;
        })
      )
      .subscribe({
        next: (data: any) => {
          const items = data?.getProgrammingServicesByIds?.items ?? [];
          const byServiceId = new Map<string, any>();
          for (const item of items) {
            byServiceId.set(String(item.id), item);
          }

          for (const itinerary of itineraries) {
            const validServiceIds = (itinerary.serviceIds ?? []).filter(
              (serviceId) => byServiceId.has(String(serviceId))
            );
            itinerary.serviceIds = validServiceIds;
            itinerary.servicios = validServiceIds
              .map((serviceId) => {
                const raw = byServiceId.get(String(serviceId));
                return raw
                  ? this.mapProgrammingService(raw, itinerary.id)
                  : null;
              })
              .filter((s): s is MassOsItineraryService => s !== null);
          }
          this.hydrateProviderConditions();
          if (advanceStep) {
            this.activeStep += 1;
          }
        },
        error: (err) => {
          console.error('Error precargando servicios', err);
          if (advanceStep) {
            this.activeStep += 1;
          }
        }
      });
  }

  /** Mapea un item de getProgrammingServicesByIds a la forma de la tabla. */
  private mapProgrammingService(
    raw: any,
    idItinerary: string
  ): MassOsItineraryService {
    let moneda: MassOsCurrency = 'PEN';
    const monedaRaw = String(raw.moneda ?? '')
      .toString()
      .toUpperCase();
    if (
      monedaRaw.includes('DOLAR') ||
      monedaRaw.includes('USD') ||
      monedaRaw === 'D'
    ) {
      moneda = 'USD';
    }

    const displayTypeService = toMassOsServiceOrigin(raw.displayTypeService);

    return {
      id: String(raw.id),
      serviceId: String(raw.id),
      idItinerary,
      serviceDay: Number(raw.day) || 1,
      fecha: this.parseServiceDate(raw.startDateService),
      tipoServicio: massOsServiceTypeLabel(raw.tipoServicio),
      nombreServicio: raw.nombreServicio ?? '',
      nombre: raw.providerSnapshot?.nombre ?? '',
      providerId: raw.providerSnapshot?.id ?? null,
      providerCondicionesPagos: raw.providerSnapshot?.condicionesPagos ?? null,
      moneda,
      pax: Number(raw.paxQuantity) || Number(raw.cantPersonal) || 0,
      paxQuantity:
        raw.paxQuantity != null && !Number.isNaN(Number(raw.paxQuantity))
          ? Number(raw.paxQuantity)
          : undefined,
      cantPersonal:
        raw.cantPersonal != null && !Number.isNaN(Number(raw.cantPersonal))
          ? Number(raw.cantPersonal)
          : undefined,
      costo: Number(raw.total) || 0,
      total: Number(raw.total) || 0,
      estadoServicioTickets: raw.estado ?? null,
      displayTypeService,
      numOS: null
    };
  }

  private parseServiceDate(value: string | null | undefined): Date {
    if (!value) {
      return new Date();
    }
    const trimmed = value.trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
    const ddmmyyyy = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/.exec(trimmed);
    if (ddmmyyyy) {
      return new Date(
        Number(ddmmyyyy[3]),
        Number(ddmmyyyy[2]) - 1,
        Number(ddmmyyyy[1])
      );
    }
    return new Date(value);
  }

  private buildProgrammingItinerariesInput(
    cursor: string | null,
    limit: number
  ): any {
    return {
      view: 'file',
      limit,
      cursor,
      sort: { field: 'updatedAt', direction: 'desc' },
      filters: {
        year: this.form.year != null ? String(this.form.year) : null,
        months: this.form.months.length ? this.form.months : undefined,
        rangeStart: this.form.rangeStart
          ? this.toDateInput(this.form.rangeStart)
          : null,
        rangeEnd: this.form.rangeEnd
          ? this.toDateInput(this.form.rangeEnd)
          : null,
        idClient: this.form.b2bClientIds,
        providerId: this.form.providerId,
        serviceTypes: this.form.serviceTypeIds
          .map((id) => MASS_OS_SERVICE_TYPE_TO_QUERY[id])
          .filter((v): v is string => !!v),
        blockBy: this.mode === 'np' ? 'PURCHASE_NOTE' : 'ORDER'
      }
    };
  }

  private toDateInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private loadProviders(): void {
    this.isLoadingProviders = true;
    this.api
      .getAllProviders()
      .pipe(
        finalize(() => {
          this.isLoadingProviders = false;
        })
      )
      .subscribe({
        next: (items) => {
          this.providers = items.map((p) => this.mapProvider(p));
        },
        error: (err) => console.error('Error cargando proveedores', err)
      });
  }

  private loadB2bClients(): void {
    this.isLoadingClients = true;
    this.fileApi
      .getSalesClientFilterOptions({ limit: 200 })
      .pipe(
        finalize(() => {
          this.isLoadingClients = false;
        })
      )
      .subscribe({
        next: (data: any) => {
          const items = data?.getSalesClientFilterOptions ?? [];
          this.b2bClients = items.map((c: any) => ({
            id: c.id,
            name: c.nameClient
          }));
        },
        error: (err) => console.error('Error cargando clientes B2B', err)
      });
  }

  private mapProvider(raw: GetAllProviderResponse): MassOsProviderOption {
    return {
      id: raw.id,
      name: raw.nombre
    };
  }

  /**
   * Las condiciones de pago del proveedor ya no vienen de getAllProviders;
   * se derivan del providerSnapshot de los servicios (getProgrammingServicesByIds).
   * Aplica las condiciones del primer servicio del proveedor seleccionado.
   */
  private hydrateProviderConditions(): void {
    const provider = this.providers.find((p) => p.id === this.form.providerId);
    if (!provider) {
      return;
    }

    for (const itinerary of this.itineraries) {
      for (const svc of itinerary.servicios) {
        if (
          (svc.providerId ?? null) !== (provider.id ?? null) ||
          !svc.providerCondicionesPagos
        ) {
          continue;
        }
        const cond = svc.providerCondicionesPagos;
        const condicionPago: MassOsPaymentCondition =
          String(cond.condicionPago ?? '').toUpperCase() === 'PREPAGO'
            ? 'PREPAGO'
            : 'POSTPAGO';
        const plazoDiasFit = cond.plazoDiasFit;
        const condicionPagoDias =
          plazoDiasFit != null && !Number.isNaN(Number(plazoDiasFit))
            ? Number(plazoDiasFit)
            : condicionPago === 'PREPAGO'
              ? 30
              : 0;
        provider.condicionPago = condicionPago;
        provider.condicionPagoDias = condicionPagoDias;
        return;
      }
    }
  }
}
